import React, { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import * as XLSX from "xlsx";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config";
import { sweetAlert } from "../../../../../utils/UniversalFunction";
import { LabelledInput } from "./SupplierManagementPrimitives";
import SupplierLookupBanner from "./SupplierLookupBanner";
import InviteTabSwitch from "./InviteTabSwitch";
import BulkUploadPanel from "./BulkUploadPanel";
import { isValidEmail } from "./supplierManagementHelpers";

const INITIAL_CREATE_FORM = {
  company_name: "",
  email: "",
  supplier_id: "",
};

const CreateInvitationModal = ({ show, onClose, onCreated }) => {
  const [tab, setTab] = useState("manual");
  const [form, setForm] = useState(INITIAL_CREATE_FORM);
  const [creating, setCreating] = useState(false);
  const [lookupState, setLookupState] = useState({ loading: false, match: null });
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    if (show) {
      setTab("manual");
      setForm(INITIAL_CREATE_FORM);
      setLookupState({ loading: false, match: null });
      setBulkFile(null);
      setBulkResult(null);
    }
  }, [show]);

  const pickedExistingSupplier = Boolean(form.supplier_id);

  useEffect(() => {
    if (!show) return undefined;
    const email = form.email?.trim() || "";
    const company_name = form.company_name?.trim() || "";
    const emailUsable = isValidEmail(email);
    if (!emailUsable && (pickedExistingSupplier || company_name.length < 3)) {
      setLookupState({ loading: false, match: null });
      return undefined;
    }
    let cancelled = false;
    setLookupState((prev) => ({ ...prev, loading: true }));
    const handle = setTimeout(async () => {
      const params = {};
      if (emailUsable) params.email = email;
      if (!pickedExistingSupplier && company_name) params.company_name = company_name;
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}suppliers/lookup`,
        {},
        params,
        "GET",
        true,
        false
      );
      if (cancelled) return;
      const match = isSuccess ? data?.data?.match ?? null : null;
      setLookupState({ loading: false, match });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [show, form.email, form.company_name, pickedExistingSupplier]);

  const setCreateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const emailAlreadyRegistered = lookupState.match?.matched_by === "email";
  const canSubmit =
    Boolean(form.company_name?.trim()) &&
    isValidEmail(form.email) &&
    !emailAlreadyRegistered &&
    !lookupState.match?.has_pending_invitation;

  const handleClose = () => {
    if (creating || bulkUploading) return;
    onClose();
  };

  const handleCreate = async () => {
    if (!canSubmit) return;
    setCreating(true);
    const payload = {
      email: form.email?.trim() || "",
      is_send_email_invitation: true,
    };
    if (pickedExistingSupplier) {
      payload.supplier_id = Number(form.supplier_id);
    } else {
      payload.company_name = form.company_name?.trim() || "";
    }
    const { isSuccess } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}suppliers/send-invitation`,
      {},
      payload,
      "POST"
    );
    setCreating(false);
    if (isSuccess) {
      onCreated?.({ switchToInvitations: true });
      onClose();
    }
  };

  const downloadBulkTemplate = () => {
    const rows = [
      ["company_name", "email"],
      ["Acme Components Pvt Ltd", "contact@acme.example.com"],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 40 }, { wch: 32 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "suppliers");
    XLSX.writeFile(wb, "supplier-invite-template.xlsx");
  };

  const handleBulkFileChange = (fileOrEvent) => {
    const file =
      fileOrEvent instanceof File
        ? fileOrEvent
        : fileOrEvent?.target?.files?.[0] || null;
    setBulkFile(file);
    setBulkResult(null);
  };

  const handleBulkFileClear = () => {
    setBulkFile(null);
    setBulkResult(null);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    setBulkResult(null);
    const formData = new FormData();
    formData.append("file", bulkFile);
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}suppliers/invitation/bulk`,
      {},
      formData,
      "POST",
      true,
      false
    );
    setBulkUploading(false);
    if (isSuccess) {
      const summary = data?.data || { total: 0, succeeded: 0, skipped: [] };
      setBulkResult(summary);
      if (summary.succeeded > 0) {
        sweetAlert(
          "success",
          `Sent ${summary.succeeded} invitation${summary.succeeded === 1 ? "" : "s"}${
            summary.skipped?.length ? `, skipped ${summary.skipped.length}` : ""
          }.`
        );
        onCreated?.({ switchToInvitations: false });
      } else if (summary.skipped?.length) {
        sweetAlert("error", `No invitations sent — ${summary.skipped.length} row(s) skipped.`);
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop={creating ? "static" : true}
      keyboard={!creating}
    >
      <Modal.Header closeButton style={{ borderBottom: "1px solid var(--rule)" }}>
        <Modal.Title style={{ fontSize: 17, fontWeight: 800, color: "var(--ink)" }}>
          Invite supplier
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "#f0f2f5" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <InviteTabSwitch active={tab} onChange={setTab} />
          {tab === "manual" ? (
            <>
              <SupplierLookupBanner state={lookupState} />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 12,
                }}
              >
                <LabelledInput
                  label={
                    pickedExistingSupplier
                      ? "Company name (existing supplier)"
                      : "Company name *"
                  }
                  value={form.company_name}
                  onChange={(v) => setCreateField("company_name", v)}
                  placeholder="Acme Components Pvt Ltd"
                  disabled={pickedExistingSupplier}
                />
                <LabelledInput
                  label="Email address *"
                  type="email"
                  value={form.email}
                  onChange={(v) => setCreateField("email", v)}
                  placeholder="contact@supplier.example.com"
                />
              </div>
            </>
          ) : (
            <BulkUploadPanel
              file={bulkFile}
              uploading={bulkUploading}
              result={bulkResult}
              onFileChange={handleBulkFileChange}
              onFileClear={handleBulkFileClear}
              onDownloadTemplate={downloadBulkTemplate}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "1px solid var(--rule)", gap: 8 }}>
        <button
          type="button"
          className="kpi-btn-secondary"
          onClick={handleClose}
          disabled={creating || bulkUploading}
        >
          Cancel
        </button>
        {tab === "manual" ? (
          creating ? (
            <button type="button" className="kpi-btn-primary" disabled>
              <Spinner animation="border" size="sm" className="me-1" />
              Sending…
            </button>
          ) : (
            <button
              type="button"
              className="kpi-btn-primary"
              onClick={handleCreate}
              disabled={!canSubmit}
              title={
                !canSubmit
                  ? "Company name and a valid email are required"
                  : undefined
              }
            >
              Invite supplier
            </button>
          )
        ) : bulkUploading ? (
          <button type="button" className="kpi-btn-primary" disabled>
            <Spinner animation="border" size="sm" className="me-1" />
            Uploading…
          </button>
        ) : (
          <button
            type="button"
            className="kpi-btn-primary"
            onClick={handleBulkUpload}
            disabled={!bulkFile}
            title={!bulkFile ? "Select a .xlsx file first" : undefined}
          >
            Upload
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CreateInvitationModal;
